import React, { useState, useEffect } from "react"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import Button from "aldoo-ra/Button"
import { EyeIcon } from "@heroicons/react/24/outline"
import { api } from "aldoo-ra/API"
import { HtmlEditorCommander } from "aldoo-ra/Commander"

const { runCommand } = HtmlEditorCommander

const NewsletterTemplateSelectorEditor = ({ 
  onChange, 
  value, 
  field, 
  parentValues, 
  parentObject 
}) => {
  const [localValue, setLocalValue] = useState(value || "")
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || "")
  }, [value])

  // Load available mail templates
  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Use CRUD API to get only system category MailTemplates  
      // Model: CMSContent, Filter: content.type = 'MailTemplate' AND content.category = 'system'
      const crudApi = api('crud')
      const response = await crudApi({
        'get': {
          model: 'CMSContent',
          match: { 
            'content.type': 'MailTemplate',
            'content.category': 'system',
            'deleted': { $ne: true }  // Exclude deleted templates
          },
          limit: 100,
          sort: { 'content.name': 1 }
        }
      })
      
      if (response.error) {
        throw new Error(response.error)
      }

      let templates = response.data || response.results || response
      
      if (!Array.isArray(templates)) {
        // If it's not an array but response has data, try different structures
        if (typeof response === 'object' && response !== null) {
          const possibleTemplatesArrays = [
            response.results,
            response.items,
            response.list,
            response.mailTemplates,
            response.MailTemplates
          ]
          
          for (const possibleArray of possibleTemplatesArrays) {
            if (Array.isArray(possibleArray)) {
              templates = possibleArray
              break
            }
          }
        }
        
        if (!Array.isArray(templates)) {
          setTemplates([])
          setError('No templates data found')
          return
        }
      }

      // No need to filter since CRUD API already returned only system templates
      const systemTemplates = templates

      const templateOptions = systemTemplates.map(template => ({
        value: template.id || template.content?.id || template._id,
        name: template.name || template.content?.name || template.id || 'Unnamed Template',
        description: template.description || template.content?.description || '',
        category: template.category || template.content?.category || 'system',
        template: template // Store full template object for reference
      }))

      setTemplates(templateOptions)
    } catch (err) {
      console.error('Failed to load mail templates:', err)
      setError(err.message)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  const handleSelectionChange = (selectedOptions) => {
    const selectedTemplate = selectedOptions?.[0] || null
    const templateId = selectedTemplate?.value || ""
    
    setLocalValue(templateId)
    onChange?.(templateId)
  }

  const handlePreviewTemplate = async (template) => {
    if (!template?.value) return
    
    try {
      // Fetch the full template content including HTML
      const crudApi = api('crud')
      const response = await crudApi({
        'get': {
          model: 'CMSContent',
          match: { 
            'content.id': template.value,
            'content.type': 'MailTemplate'
          },
          limit: 1
        }
      })
      
      if (response && response.length > 0) {
        const templateData = response[0]
        
        // Check different possible paths for HTML content
        const htmlContent = 
          templateData.content?.html || 
          templateData.content?.source || 
          templateData.html || 
          templateData.source || 
          null
        
        if (htmlContent) {
          // Use the same preview system as the HTML editor, but hide HTML editor option
          runCommand("pagePreview.toggle", {
            getContent: () => htmlContent,
            openHtmlEditor: null, // Hide HTML editor option by setting to null
          })
        } else {
          // Navigate to template editor to view/edit HTML content
          const templateEditUrl = `/admin/mail-templates/edit/${template.value}`
          window.open(templateEditUrl, '_blank')
        }
      }
    } catch (error) {
      console.error('Failed to load template content:', error)
    }
  }

  // Find selected template for display
  const selectedTemplate = templates.find(t => t.value === localValue)
  
  
  // If we have a value but no matching template, create a placeholder option
  let selectedOptions = []
  if (selectedTemplate) {
    selectedOptions = [selectedTemplate]
  } else if (localValue && localValue !== "" && !loading) {
    // Create a placeholder option for unknown template IDs
    selectedOptions = [{
      value: localValue,
      name: localValue,
      description: 'Template not found in current list',
      category: 'unknown'
    }]
  }

  const isRequired = typeof field.required === "function" 
    ? field.required(parentObject) 
    : field.required

  const getCategoryBadgeClass = (category) => {
    const categoryColors = {
      'system': 'bg-admin_info text-white',
      'user': 'bg-admin_success text-white',
      'newsletter': 'bg-admin_warning text-white',
      'unknown': 'bg-admin_error text-white'
    }
    return categoryColors[category] || 'bg-neutral_3 text-white'
  }

  return (
    <div className="newsletter-template-selector w-full">
      <div className="flex flex-col space-y-2">
        {error ? (
          <div className="border border-admin_error/30 bg-admin_error/10 rounded-lg p-4">
            <Typography 
              variant="admin_body" 
              className="text-admin_error"
            >
              Error loading templates: {error}
            </Typography>
          </div>
        ) : (
          <DropdownSelect
            variant="admin"
            options={templates}
            value={localValue === "" ? [] : selectedOptions}
            onChange={handleSelectionChange}
            multiSelection={false}
            searchEnabled={true}
            showCheckbox={false}
            showSelectionTick={true}
            placeholder={loading ? "Loading system templates..." : "Please select a template"}
            disabled={loading}
            searchPlaceholder="Search system templates by name, ID, or description..."
            onItemRender={(template) => (
              <Tooltip 
                content={template.description || 'No description available'} 
                placement="right"
                className="w-full"
              >
                <div className="w-full">
                  <Typography 
                    variant="admin_label" 
                    className="font-medium text-admin_text my-0"
                  >
                    {template.name}
                  </Typography>
                </div>
              </Tooltip>
            )}
            onValueItemRender={(template) => (
              <div>
                <Typography variant="admin_body" className="text-admin_text">
                  {template?.name || (localValue && localValue !== "" ? localValue : 'Please select a template')}
                </Typography>
              </div>
            )}
            // Custom search function that searches name, ID, and description
            customSearchFilter={(template, searchQuery) => {
              const query = searchQuery.toLowerCase()
              const name = template.name?.toLowerCase() || ''
              const id = template.value?.toLowerCase() || ''
              const description = template.description?.toLowerCase() || ''
              
              return name.includes(query) || id.includes(query) || description.includes(query)
            }}
          />
        )}

        {/* Show description for selected template */}
        {selectedTemplate && selectedTemplate.description && (
          <div className="bg-admin_paper rounded-lg border border-admin_bordered p-4 mt-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Typography 
                  variant="admin_body_dim" 
                  className="leading-relaxed"
                >
                  {selectedTemplate.description}
                </Typography>
              </div>

              <div className="flex-shrink-0">
                <Button
                  variant="admin_ghost"
                  className="flex items-center gap-2 hover:bg-admin_neutral_2 transition-colors px-3 py-2"
                  onClick={() => handlePreviewTemplate(selectedTemplate)}
                >
                  <EyeIcon className="w-4 h-4" />

                  <span className="text-sm font-medium">
                    Preview
                  </span>
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && templates.length === 0 && !error && (
          <Typography 
            variant="admin_body_dim" 
            className="my-0"
          >
            No system mail templates found. Create system templates in the Mail Templates section first.
          </Typography>
        )}
      </div>
    </div>
  )
}

export default NewsletterTemplateSelectorEditor